//#region IMPORTS
import {
  setPageNumber,
  setSelectedOrders,
} from "../../features/orders/ordersSlice";
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { Order, ORDERSTATUS, ORDERTYPE } from "../../models/order";
import { MetaData } from "../../models/pagination";
import OrdersTableHeader from "./OrdersTableHeader";
import usePickerManager from "../../hooks/usePickerManager";
import useAdminManager from "../../hooks/useAdminManager";
import OrdersTableRow from "./OrdersTableRow";
import useHQmanager from "../../hooks/useHQmanager";
import MainTable from "../_layout/MainTable";
import PaginationBarClient from "../paginationBar/PaginationBarClient";
//#endregion

//#region INTERFACE
interface Props {
  changeOrderStatusHandler: (order: Order, status: string) => void;
  downloadPicklistHandler?: (order: Order) => void;
  itemsLoaded?: boolean;
  clientPaginate?: boolean;
  metaData?: MetaData;
  items: Order[];
  hide?: boolean;
}
//#endregion

export default function OrdersTable({
  changeOrderStatusHandler,
  downloadPicklistHandler,
  itemsLoaded = false,
  clientPaginate = false,
  metaData,
  items = [],
  hide = false,
}: Props) {
  //#region SETUP
  // Ref
  const checkbox = useRef<any>();

  // Redux
  const dispatch = useAppDispatch();
  const { selectedOrders } = useAppSelector((state) => state.orders);
  const { user } = useAppSelector((state) => state.account);

  // Roles
  const isPicker = usePickerManager(user);
  const isHQTeam = useHQmanager(user);
  const isAdmin = useAdminManager(user);

  // Local state
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);

  // Orders that may be selected. Cancelled and Original-type orders are never
  // actionable, so they are excluded from select-all + the row checkbox.
  const selectableItems = items.filter((order) =>
    isAdmin || isPicker
      ? !!order.promo_id &&
        order.order_type !== ORDERTYPE.original &&
        order.order_status !== ORDERSTATUS.cancelled
      : order.order_type !== ORDERTYPE.original &&
        order.order_status !== ORDERSTATUS.cancelled &&
        (order.order_status === ORDERSTATUS.pending ||
          order.order_status === ORDERSTATUS.ordered),
  );

  // Client-side pagination (picker "Manage": all orders are fetched at once)
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const pagedItems = clientPaginate
    ? items.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize)
    : items;

  // Decline (approve/decline toggle) only applies to pending orders, so the
  // whole column is dropped on a page that has none.
  const pageHasApprovable = pagedItems.some(
    (order) =>
      order.order_type !== ORDERTYPE.original &&
      order.order_status === ORDERSTATUS.pending,
  );
  //#endregion

  //#region LOGIC
  useEffect(() => {
    if (!itemsLoaded) {
      dispatch(setSelectedOrders([]));
    }
  }, [itemsLoaded, dispatch]);

  // Keep the current page in range when the list shrinks (e.g. after a status
  // change removes a row from the filtered set)
  useEffect(() => {
    if (pageIndex > pageCount - 1) setPageIndex(pageCount - 1);
  }, [pageCount, pageIndex]);

  // Change table on the fly
  useLayoutEffect(() => {
    const total = selectableItems.length;
    const isIndeterminate =
      selectedOrders.length > 0 && selectedOrders.length < total;
    setChecked(selectedOrders.length > 0 && selectedOrders.length === total);
    setIndeterminate(isIndeterminate);
    if (isAdmin || isPicker || isHQTeam) {
      if (checkbox.current) {
        checkbox.current.indeterminate = isIndeterminate;
      }
    }
  }, [
    selectedOrders,
    selectableItems.length,
    checkbox,
    isPicker,
    isAdmin,
    isHQTeam,
  ]);

  // Toggle all selectable orders
  const toggleAll = () => {
    dispatch(setSelectedOrders(checked || indeterminate ? [] : selectableItems));
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  };
  //#endregion

  //#region RENDER
  return (
    <Fragment>
    <MainTable
      itemsLoaded={itemsLoaded}
      clientPaginate={clientPaginate}
      metaData={metaData}
      pageType="orders"
      onPageChangeHandler={(page: number) =>
        dispatch(setPageNumber({ page: page }))
      }
      header={
        <OrdersTableHeader
          toggleAll={toggleAll}
          checked={checked}
          pageHasApprovable={pageHasApprovable}
        />
      }
      hide={hide}
    >
      {pagedItems.map((order) => (
        <OrdersTableRow
          clearSelectedOrdersHandler={() => dispatch(setSelectedOrders([]))}
          changeOrderStatusHandler={(order: Order, status: string) =>
            changeOrderStatusHandler(order, status)
          }
          downloadPicklistHandler={downloadPicklistHandler}
          selectOrderHandler={(e: any, order: Order) =>
            dispatch(
              setSelectedOrders(
                e.target.checked
                  ? [...selectedOrders, order]
                  : selectedOrders.filter((o) => o !== order)
              )
            )
          }
          selectedOrders={selectedOrders}
          isPicker={isPicker}
          isHQTeam={isHQTeam}
          isAdmin={isAdmin}
          pageHasApprovable={pageHasApprovable}
          order={order}
          key={order.id}
        />
      ))}
    </MainTable>
      {clientPaginate && !hide && (
        <PaginationBarClient
          totalAmmount={items.length}
          pageSize={pageSize}
          pageCount={pageCount}
          currentPage={pageIndex + 1}
          prevPageAvailable={pageIndex > 0}
          nextPageAvailable={pageIndex < pageCount - 1}
          firstPageHandler={() => setPageIndex(0)}
          prevPageHandler={() => setPageIndex((p) => Math.max(0, p - 1))}
          nextPageHandler={() =>
            setPageIndex((p) => Math.min(pageCount - 1, p + 1))
          }
          lastPageHandler={() => setPageIndex(pageCount - 1)}
          changePageSizeHandler={(size: number) => {
            setPageSize(size);
            setPageIndex(0);
          }}
          type="orders"
        />
      )}
    </Fragment>
  );
  //#endregion
}
